import { createContext, useContext, useState, useEffect, ReactNode } from 'react'

interface SidebarContextType {
  collapsed: boolean
  setCollapsed: (collapsed: boolean) => void
  toggleCollapsed: () => void
}

const SidebarContext = createContext<SidebarContextType | null>(null)

// Breakpoint para auto-collapse (tablet ~900px)
const TABLET_BREAKPOINT = 900

export function SidebarProvider({ children }: { children: ReactNode }) {
  const [collapsed, setCollapsed] = useState(() => {
    // Inicializar baseado no tamanho da tela
    if (typeof window !== 'undefined') {
      return window.innerWidth < TABLET_BREAKPOINT
    }
    return false
  })
  const [userManuallyToggled, setUserManuallyToggled] = useState(false)

  // Auto-collapse baseado no tamanho da tela
  useEffect(() => {
    const handleResize = () => {
      const isTabletOrSmaller = window.innerWidth < TABLET_BREAKPOINT

      // Só auto-collapse se o usuário não tocou manualmente
      if (!userManuallyToggled) {
        setCollapsed(isTabletOrSmaller)
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [userManuallyToggled])

  // Reset manual toggle quando muda de desktop para tablet ou vice-versa
  useEffect(() => {
    const handleResize = () => {
      const isTabletOrSmaller = window.innerWidth < TABLET_BREAKPOINT
      // Se passou de desktop para tablet (ou vice-versa), resetar toggle manual
      if (isTabletOrSmaller !== collapsed && userManuallyToggled) {
        setUserManuallyToggled(false)
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [collapsed, userManuallyToggled])

  const toggleCollapsed = () => {
    setUserManuallyToggled(true)
    setCollapsed((prev) => !prev)
  }

  return (
    <SidebarContext.Provider value={{ collapsed, setCollapsed, toggleCollapsed }}>
      {children}
    </SidebarContext.Provider>
  )
}

export function useSidebar() {
  const context = useContext(SidebarContext)
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider')
  }
  return context
}
